import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  Input,
  MenuItem,
  Select,
  Popover,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import toast from 'react-hot-toast';
import Layout from '../../../../components/Layout';
import AdminNavbar from '../../../../components/Admin/AdminNavbar/AdminNavbar';
import {
  EligiblePaymentsTable,
  SOURCE_LABEL,
} from '../../../../components/Admin/Payments/EFT/EligiblePaymentsTable';
import { TransferredTable } from '../../../../components/Admin/Payments/EFT/TransferredTable';
import { TransactionDetailsModal } from '../../../../components/Admin/Payments/EFT/TransactionDetailsModal';
import { axiosInstance, getErrorDetail } from '../../../../utils/tokenUtils';
import ColoredFilterIcon from '../../../../assets/Admin/RequestImages/colored-filter-icon.svg';
import FilterIcon from '../../../../assets/Admin/RequestImages/filter-icon.svg';
import { toLocalTime } from '../../../../utils/dateUtils';
import './EFT.css';

const ELIGIBLE_TAB = 0;
const TRANSFERRED_TAB = 1;

/**
 * API timestamps are naive UTC ("2026-10-26T08:42:00", no "Z"). new Date()
 * would read that as local time and show the UTC clock, so parse as UTC and
 * convert to the viewer's timezone.
 */
const formatTimestamp = (iso, format) => {
  const date = iso && toLocalTime(iso);
  return date && date.isValid() ? date.format(format) : '-';
};

/** dd-MM-yyyy, matching the rest of the payment screens. */
const formatDate = iso => formatTimestamp(iso, 'DD-MM-YYYY');

/** The API takes plain yyyy-MM-dd; sending an ISO timestamp shifts the day. */
const toApiDate = date => {
  if (!date) return undefined;
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return undefined;
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${parsed.getFullYear()}-${month}-${day}`;
};

const EMPTY_TRANSFER_FILTERS = {
  status: '',
  startDate: '',
  endDate: '',
  minAmount: '',
  maxAmount: '',
};

/** Transferred-tab filters as query params; dates are already yyyy-MM-dd. */
const toTransferParams = filters => ({
  status: filters.status || undefined,
  start_date: filters.startDate || undefined,
  end_date: filters.endDate || undefined,
  min_total_amount: filters.minAmount === '' ? undefined : filters.minAmount,
  max_total_amount: filters.maxAmount === '' ? undefined : filters.maxAmount,
});

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** dd MMM yyyy, the format the Transferred tab is designed around. */
const formatLongDate = iso => formatTimestamp(iso, 'DD MMM YYYY');

/**
 * dd MMM yyyy for a calendar date the admin picked (the Transaction Date).
 * Stored as that day's midnight, so it is shown as-is: converting it would
 * move the day for anyone west of UTC.
 */
const formatCalendarDate = iso => {
  if (!iso) return '-';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '-';
  const day = String(date.getDate()).padStart(2, '0');
  return `${day} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
};

const formatAmount = value =>
  Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/**
 * Card figures: "12,210 Riyal" for whole amounts, otherwise always two
 * decimals ("509,092.50"), so the card matches the QAR total beside the table.
 */
const formatCardAmount = value => {
  const amount = Number(value || 0);
  const decimals = Number.isInteger(amount) ? 0 : 2;
  return `${amount.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: 2,
  })} Riyal`;
};

const useDebounce = (value, delay) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debounced;
};

const EFT = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Coming back from a detail page should land on the tab you left from.
  const [activeTab, setActiveTab] = useState(location?.state?.activeTab ?? ELIGIBLE_TAB);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [eligibleRows, setEligibleRows] = useState([]);
  const [summary, setSummary] = useState({ total_count: 0, total_amount: 0 });
  const [transfers, setTransfers] = useState([]);
  // Page-wide totals for the cards; not tied to either tab's filters.
  const [overview, setOverview] = useState(null);
  const [rbacMe, setRbacMe] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // id -> numeric amount, so the running total survives paging.
  const [selected, setSelected] = useState({});
  // Select All is a mode, not a list: it stands for the whole filtered set.
  const [allMatchingSelected, setAllMatchingSelected] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Transferred tab: its own filters, actions and modals.
  // Applied filters drive the fetch; the popover edits a draft until Apply.
  const [transferFilters, setTransferFilters] = useState(EMPTY_TRANSFER_FILTERS);
  const [uiTransferFilters, setUiTransferFilters] = useState(EMPTY_TRANSFER_FILTERS);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [editTransfer, setEditTransfer] = useState(null);
  const [deleteTransfer, setDeleteTransfer] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  // Both tabs share the table state, so only the newest request may write to
  // it; a slower, older response would otherwise overwrite the current one.
  const latestRequestRef = useRef(0);

  const commonParams = useMemo(
    () => ({
      start_date: toApiDate(startDate),
      end_date: toApiDate(endDate),
      search_query: debouncedSearch?.trim() || undefined,
      page: currentPage,
      page_size: pageSize,
    }),
    [startDate, endDate, debouncedSearch, currentPage, pageSize]
  );

  const fetchEligible = useCallback(async () => {
    const requestId = ++latestRequestRef.current;
    try {
      setIsLoading(true);
      const { data } = await axiosInstance.get('/admin/eft/eligible-payments', {
        params: commonParams,
      });
      if (requestId !== latestRequestRef.current) return;
      setEligibleRows(
        (data?.results || []).map(item => ({
          id: item.source_payment_id,
          payerName: item.payer?.name || '-',
          payerImage: item.payer?.profile_image || '',
          payerEmail: item.payer?.email || '',
          payerPhone: item.payer?.phone_no || '',
          paymentFor: SOURCE_LABEL[item.source_type] || item.source_type,
          paidAt: formatDate(item.paid_at),
          eligibleAt: formatDate(item.eligible_at),
          amount: formatAmount(item.amount),
          amountValue: Number(item.amount || 0),
        }))
      );
      setSummary(data?.summary || { total_count: 0, total_amount: 0 });
      setTotalCount(Number(data?.count || 0));
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      toast.error(getErrorDetail(error, 'Failed to fetch eligible payments'));
    } finally {
      if (requestId === latestRequestRef.current) setIsLoading(false);
    }
  }, [commonParams]);

  const fetchTransfers = useCallback(async () => {
    const requestId = ++latestRequestRef.current;
    try {
      setIsLoading(true);
      const { data } = await axiosInstance.get('/admin/eft/transfers', {
        // The header date pickers belong to the Eligible tab; the claim-date
        // range here comes from the filter popover instead.
        params: {
          ...commonParams,
          ...toTransferParams(transferFilters),
        },
      });
      if (requestId !== latestRequestRef.current) return;
      setTransfers(
        (data?.results || []).map(item => ({
          id: item.id,
          referenceNo: item.reference_no,
          status: item.status,
          itemCount: item.item_count,
          totalAmount: formatAmount(item.total_amount),
          // The date the Start/End Date filter matches on.
          createdAt: formatLongDate(item.created_at),
          earliestPaymentDate: formatLongDate(item.earliest_payment_at),
          latestPaymentDate: formatLongDate(item.latest_payment_at),
          bankReference: item.transfer_reference_id || '',
          transferredAt: item.transferred_at ? formatCalendarDate(item.transferred_at) : '',
          // Kept raw so the edit form can prefill its date picker.
          transferredAtRaw: item.transferred_at || null,
        }))
      );
      setTotalCount(Number(data?.count || 0));
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      toast.error(getErrorDetail(error, 'Failed to fetch transfers'));
    } finally {
      if (requestId === latestRequestRef.current) setIsLoading(false);
    }
  }, [commonParams, transferFilters]);

  const fetchOverview = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/admin/eft/summary');
      setOverview(data);
    } catch (error) {
      toast.error(getErrorDetail(error, 'Failed to fetch EFT summary'));
    }
  }, []);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  // Load current user permissions
  useEffect(() => {
    const fetchCurrentUser = async () => {
      let cachedData = null;
      try {
        const cached = sessionStorage.getItem('rbac_admin_me');
        if (cached) {
          cachedData = JSON.parse(cached);
          setRbacMe(cachedData);
        }
      } catch (_) {}

      try {
        const { data } = await axiosInstance.get('/admin/rbac/admin/me');
        setRbacMe(data);
        try {
          sessionStorage.setItem('rbac_admin_me', JSON.stringify(data));
        } catch (_) {}
      } catch (_) {
        if (!cachedData) setRbacMe({ roles: [], permissions: [] });
      }
    };

    fetchCurrentUser();
  }, []);

  const hasPermission = permission =>
    Array.isArray(rbacMe?.permissions) && rbacMe.permissions.includes(permission);
  // Admins hold these through their role; a finance officer only holds
  // delete_eft once it has been granted to them individually in RBAC.
  const canManageEft = hasPermission('manage_eft');
  const canDeleteEft = hasPermission('delete_eft');

  // Only mention the actions this admin actually has on the Transferred tab.
  const transferredSubtitle = [
    'EFTs claimed from eligible payments, with their bank transfer status.',
    canDeleteEft && 'Deleting an EFT returns its payments to the Eligible tab.',
    !canDeleteEft && canManageEft && 'Add transaction details once the bank transfer is done.',
  ]
    .filter(Boolean)
    .join(' ');

  useEffect(() => {
    if (activeTab === ELIGIBLE_TAB) {
      fetchEligible();
    } else {
      fetchTransfers();
    }
  }, [activeTab, fetchEligible, fetchTransfers]);

  // Any filter change invalidates the page number and the ticked rows: those
  // ids may not be in the new result set at all.
  //
  // A complete date range also selects everything it matches, because that is
  // exactly what Claim will act on -- leaving the boxes empty while the button
  // is armed hides what is about to be claimed.
  useEffect(() => {
    setCurrentPage(1);
    setSelected({});
    setAllMatchingSelected(Boolean(startDate && endDate));
  }, [debouncedSearch, startDate, endDate, activeTab]);

  const selectedIds = useMemo(() => Object.keys(selected), [selected]);

  const selectedTotal = useMemo(
    () => Object.values(selected).reduce((sum, amount) => sum + amount, 0),
    [selected]
  );

  const handleToggleOne = row => {
    if (allMatchingSelected) {
      setAllMatchingSelected(false);
      setSelected(
        Object.fromEntries(
          eligibleRows.filter(item => item.id !== row.id).map(item => [item.id, item.amountValue])
        )
      );
      return;
    }
    setSelected(previous => {
      const next = { ...previous };
      if (next[row.id] === undefined) {
        next[row.id] = row.amountValue;
      } else {
        delete next[row.id];
      }
      return next;
    });
  };

  const handleToggleAll = checked => {
    setAllMatchingSelected(checked);
    setSelected({});
  };

  // With nothing ticked the claim takes the whole filtered set, which the API
  // only accepts when both dates are present -- otherwise it would sweep every
  // eligible payment ever recorded.
  const claimsWholeFilter = allMatchingSelected || selectedIds.length === 0;
  const canClaim =
    activeTab === ELIGIBLE_TAB &&
    !isClaiming &&
    summary.total_count > 0 &&
    // Select All is itself the explicit opt-in, so it needs no date range.
    (allMatchingSelected || !claimsWholeFilter || (startDate && endDate));

  const claimCount = claimsWholeFilter ? summary.total_count : selectedIds.length;
  // The figure beside Claim must be the figure Claim acts on.
  const displayTotal = claimsWholeFilter ? summary.total_amount : selectedTotal;
  const hasSelection = allMatchingSelected || selectedIds.length > 0;

  const handleClaimClick = () => {
    if (activeTab !== ELIGIBLE_TAB) return;
    if (claimsWholeFilter && !allMatchingSelected && !(startDate && endDate)) {
      toast.error('Pick a date range, or tick the payments you want to claim.');
      return;
    }
    if (claimCount === 0) {
      toast.error('There is nothing to claim for these filters.');
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmClaim = async () => {
    setConfirmOpen(false);
    const toastId = toast.loading('Claiming payments...');
    try {
      setIsClaiming(true);
      const { data } = await axiosInstance.post('/admin/eft/transfers', {
        start_date: toApiDate(startDate),
        end_date: toApiDate(endDate),
        // Select All stands for the searched list, not every eligible payment.
        search_query: debouncedSearch?.trim() || undefined,
        payment_ids: allMatchingSelected || !selectedIds.length ? undefined : selectedIds,
        claim_all: allMatchingSelected,
      });
      const rejected = data?.rejected?.length || 0;
      const payments = `${data.item_count} payment${data.item_count === 1 ? '' : 's'}`;
      toast.success(
        <span>
          <strong>EFT created</strong>
          <br />
          {data.reference_no} · {payments} · QAR {formatAmount(data.total_amount)}
          {rejected ? ` · ${rejected} could not be claimed` : ''}
        </span>
      );
      setSelected({});
      setAllMatchingSelected(false);
      // Straight to the new transfer: the admin's next step is recording the
      // bank transaction against it, not scanning the list for it.
      navigate(`/admin/payments/eft-details/${data.id}`);
    } catch (error) {
      toast.error(getErrorDetail(error, 'Failed to claim payments'));
    } finally {
      toast.dismiss(toastId);
      setIsClaiming(false);
    }
  };

  // ---------------------------------------------------------------- //
  // Transferred tab actions
  // ---------------------------------------------------------------- //

  const handleSaveTransaction = async ({ status, reference, transactionDate }) => {
    const toastId = toast.loading('Saving transaction details...');
    try {
      setIsSaving(true);
      await axiosInstance.patch(`/admin/eft/transfers/${editTransfer.id}`, {
        status,
        transfer_reference_id: reference || undefined,
        // Already a naive "YYYY-MM-DDT00:00:00" string from the modal; the
        // API defaults to now when it is absent.
        transferred_at: transactionDate || undefined,
      });
      toast.success('Transaction details saved');
      setEditTransfer(null);
      fetchTransfers();
      fetchOverview();
    } catch (error) {
      toast.error(getErrorDetail(error, 'Failed to save transaction details'));
    } finally {
      toast.dismiss(toastId);
      setIsSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    const target = deleteTransfer;
    setDeleteTransfer(null);
    const toastId = toast.loading('Deleting transfer...');
    try {
      await axiosInstance.delete(`/admin/eft/transfers/${target.id}`);
      toast.success(`${target.referenceNo} deleted. Its payments are eligible again.`);
      fetchTransfers();
      fetchOverview();
    } catch (error) {
      toast.error(getErrorDetail(error, 'Failed to delete transfer'));
    } finally {
      toast.dismiss(toastId);
    }
  };

  // Both downloads return an Azure blob URL rather than streaming a file.
  const openDownload = url => {
    const link = document.createElement('a');
    link.href = url;
    link.download = '';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadList = async () => {
    const toastId = toast.loading('Preparing download...');
    try {
      setIsDownloading(true);
      const { data } = await axiosInstance.get('/admin/eft/transfers/download', {
        params: {
          search_query: debouncedSearch?.trim() || undefined,
          ...toTransferParams(transferFilters),
        },
      });
      if (data?.url) {
        openDownload(data.url);
        toast.success('Downloaded successfully');
      } else {
        toast.error('No download URL returned');
      }
    } catch (error) {
      toast.error(getErrorDetail(error, 'Failed to start download'));
    } finally {
      toast.dismiss(toastId);
      setIsDownloading(false);
    }
  };

  const openTransferFilter = event => {
    setUiTransferFilters(transferFilters);
    setFilterAnchor(event.currentTarget);
  };

  const closeTransferFilter = () => setFilterAnchor(null);

  const updateUiTransferFilter = (key, value) =>
    setUiTransferFilters(prev => ({ ...prev, [key]: value }));

  const applyTransferFilters = () => {
    const { startDate: from, endDate: to, minAmount: min, maxAmount: max } = uiTransferFilters;
    // A backwards range would silently return nothing; tell the user instead.
    if (from && to && from > to) {
      toast.error('Start date must be on or before the end date');
      return;
    }
    if (min !== '' && max !== '' && Number(min) > Number(max)) {
      toast.error('Min amount must not be more than the max amount');
      return;
    }
    setTransferFilters(uiTransferFilters);
    setCurrentPage(1);
    closeTransferFilter();
  };

  const clearTransferFilters = () => setUiTransferFilters(EMPTY_TRANSFER_FILTERS);

  const paginationItems = useMemo(() => {
    const items = [];
    if (totalPages <= 1) return items;
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i += 1) items.push(i);
      return items;
    }
    items.push(1);
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    if (start > 2) items.push('...');
    for (let i = start; i <= end; i += 1) items.push(i);
    if (end < totalPages - 1) items.push('...');
    items.push(totalPages);
    return items;
  }, [totalPages, currentPage]);

  const datePickerSx = {
    width: '190px',
    '& .MuiOutlinedInput-root': {
      height: '40px',
      borderRadius: '8px',
      fontFamily: 'Poppins',
      fontSize: '14px',
      '& fieldset': { border: '1px solid var(--Stroke, #D5DEE3)' },
      '&:hover fieldset': { border: '1px solid var(--Stroke, #D5DEE3)' },
      '&.Mui-focused fieldset': { border: '1px solid #0a5ef3' },
    },
  };

  return (
    <Layout title="Klass Online | EFT">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <AdminNavbar />
        </Box>
        <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
          <Box className="flex flex-col p-[1rem] gap-4">
            <Box className="flex justify-between items-center flex-wrap gap-4">
              <div className="eft-title">EFT</div>
              <Box className="flex items-center gap-3 flex-wrap">
                {activeTab === ELIGIBLE_TAB && (
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={setStartDate}
                      slotProps={{
                        textField: { size: 'small', sx: datePickerSx },
                        field: { clearable: true },
                      }}
                    />
                    <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={setEndDate}
                      minDate={startDate || undefined}
                      slotProps={{
                        textField: { size: 'small', sx: datePickerSx },
                        field: { clearable: true },
                      }}
                    />
                  </LocalizationProvider>
                )}

                {activeTab === ELIGIBLE_TAB && (startDate || endDate) && (
                  <Tooltip title="Clear date range">
                    <IconButton
                      className="eft-clear-dates-btn"
                      aria-label="Clear date range"
                      onClick={() => {
                        setStartDate(null);
                        setEndDate(null);
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}

                <Input
                  sx={{
                    border: '1px solid var(--Stroke, #D5DEE3)',
                    borderRadius: '56px',
                    '&:before, &:after': { borderBottom: 'none !important' },
                    '&:hover:not(.Mui-disabled):before': {
                      borderBottom: 'none !important',
                    },
                    padding: '8px 10px',
                    '& .MuiInputBase-input': { padding: '3px 5px' },
                    width: '320px',
                    height: '40px',
                  }}
                  type="search"
                  placeholder={
                    activeTab === ELIGIBLE_TAB
                      ? 'Search by Payment ID, Learner or Instructor'
                      : 'Search by EFT or bank reference'
                  }
                  aria-label="Search"
                  value={searchTerm}
                  onChange={event => setSearchTerm(event.target.value)}
                  startAdornment={<SearchIcon className="eft-search-icon" />}
                />

                {activeTab === TRANSFERRED_TAB && (
                  <img
                    src={filterAnchor ? ColoredFilterIcon : FilterIcon}
                    alt="filter"
                    onClick={filterAnchor ? closeTransferFilter : openTransferFilter}
                    style={{ width: 25, height: 25, cursor: 'pointer' }}
                  />
                )}

                {activeTab === TRANSFERRED_TAB && (
                  <button
                    type="button"
                    className={`eft-claim-btn ${
                      totalCount === 0 || isDownloading ? 'eft-claim-btn-disabled' : ''
                    }`}
                    onClick={handleDownloadList}
                    disabled={totalCount === 0 || isDownloading}
                  >
                    <DownloadIcon sx={{ fontSize: 18, color: 'inherit' }} />
                    <span>{isDownloading ? 'Preparing...' : 'Download'}</span>
                  </button>
                )}

                {activeTab === ELIGIBLE_TAB && canManageEft && (
                  <button
                    type="button"
                    className={`eft-claim-btn ${!canClaim ? 'eft-claim-btn-disabled' : ''}`}
                    onClick={handleClaimClick}
                    disabled={!canClaim}
                  >
                    <FormatListBulletedIcon sx={{ fontSize: 18, color: 'inherit' }} />
                    <span>{isClaiming ? 'Claiming...' : 'Claim'}</span>
                  </button>
                )}
              </Box>
            </Box>

            <Box className="eft-overview-cards">
              {[
                {
                  label: 'Eligible Transfers Amount',
                  value: overview && formatCardAmount(overview.eligible_amount),
                  variant: 'eft-overview-card-primary',
                  hint: 'Total amount available to claim',
                },
                {
                  label: 'Eligible Transfers',
                  value: overview?.eligible_count,
                  variant: 'eft-overview-card-blue',
                  hint: 'Payments eligible for claiming',
                },
                {
                  label: 'Pending Amount',
                  value: overview && formatCardAmount(overview.pending_amount),
                  variant: 'eft-overview-card-pending',
                  hint: 'Amount claimed but not yet transferred',
                },
                {
                  label: 'Pending Transfers',
                  value: overview?.pending_count,
                  variant: 'eft-overview-card-pending',
                  hint: 'EFTs awaiting bank transfer confirmation',
                },
              ].map(card => (
                <Box key={card.label} className={`eft-overview-card ${card.variant}`}>
                  <Typography className="eft-overview-label">{card.label}</Typography>
                  <Typography className="eft-overview-value">{card.value ?? '-'}</Typography>
                  <Typography className="eft-overview-hint">{card.hint}</Typography>
                </Box>
              ))}
            </Box>

            <Box className="eft-tabs-container">
              <Tabs
                value={activeTab}
                onChange={(event, value) => {
                  // Reset the page in the same render, so the new tab is not
                  // first fetched with the old tab's page number.
                  setCurrentPage(1);
                  setActiveTab(value);
                }}
                sx={{ minHeight: 36 }}
              >
                <Tab label="Eligible" sx={{ textTransform: 'none', minHeight: 36 }} />
                <Tab label="Transferred" sx={{ textTransform: 'none', minHeight: 36 }} />
              </Tabs>
            </Box>

            <Box className="eft-card">
              <Box className="eft-card-header">
                <Box>
                  <Typography className="eft-card-title">
                    {activeTab === ELIGIBLE_TAB ? 'Eligible Payments' : 'Transferred EFTs'}
                  </Typography>
                  <Typography className="eft-card-subtitle">
                    {activeTab === ELIGIBLE_TAB
                      ? 'These payments are eligible for EFT but not yet claimed.'
                      : transferredSubtitle}
                  </Typography>
                </Box>

                {activeTab === ELIGIBLE_TAB && hasSelection && (
                  <Box className="eft-total-card">
                    <Typography className="eft-total-label">Total Amount</Typography>
                    <Typography className="eft-total-value">
                      QAR {formatAmount(displayTotal)}
                    </Typography>
                  </Box>
                )}
              </Box>

              {activeTab === ELIGIBLE_TAB ? (
                <EligiblePaymentsTable
                  data={eligibleRows}
                  isLoading={isLoading}
                  selectedIds={selectedIds}
                  allSelected={allMatchingSelected}
                  onToggleOne={handleToggleOne}
                  onToggleAll={handleToggleAll}
                />
              ) : (
                <TransferredTable
                  data={transfers}
                  isLoading={isLoading}
                  onView={row => navigate(`/admin/payments/eft-details/${row.id}`)}
                  // Left out without the permission; the table hides that action.
                  onEdit={canManageEft ? setEditTransfer : undefined}
                  onDelete={canDeleteEft ? setDeleteTransfer : undefined}
                />
              )}

              <Box className="eft-pagination-container">
                <Box className="eft-pagination-box">
                  <Typography className="eft-pagination-text">Showing</Typography>
                  <Select
                    value={pageSize}
                    onChange={event => {
                      setPageSize(event.target.value);
                      setCurrentPage(1);
                    }}
                    sx={{
                      height: '30px',
                      minWidth: '60px',
                      margin: '0 4px',
                      '& .MuiSelect-select': { padding: '6px 24px 6px 8px' },
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={15}>15</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={30}>30</MenuItem>
                  </Select>
                  <Typography className="eft-pagination-text">
                    of {totalCount} {activeTab === ELIGIBLE_TAB ? 'eligible payments' : 'EFTs'}
                  </Typography>
                </Box>

                {paginationItems.length > 0 && (
                  <nav>
                    <ul className="eft-pagination-list">
                      <li>
                        <button
                          type="button"
                          className={`eft-pagination-btn ${currentPage === 1 ? 'eft-pagination-btn-disabled' : ''}`}
                          onClick={() => setCurrentPage(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                      </li>
                      {paginationItems.map((item, index) => (
                        <li key={`${item}-${index}`}>
                          {item === '...' ? (
                            <span className="eft-pagination-ellipsis">…</span>
                          ) : (
                            <button
                              type="button"
                              className={`eft-pagination-btn ${currentPage === item ? 'eft-pagination-btn-selected' : ''}`}
                              onClick={() => setCurrentPage(item)}
                            >
                              {item}
                            </button>
                          )}
                        </li>
                      ))}
                      <li>
                        <button
                          type="button"
                          className={`eft-pagination-btn ${currentPage === totalPages ? 'eft-pagination-btn-disabled' : ''}`}
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Popover
        open={Boolean(filterAnchor)}
        anchorEl={filterAnchor}
        onClose={closeTransferFilter}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ className: 'eft-filter-modal-container' }}
      >
        <Box className="eft-filter-modal-header">
          <Typography className="eft-filter-modal-title">Filters</Typography>
          <CloseIcon
            onClick={closeTransferFilter}
            sx={{
              cursor: 'pointer',
              color: '#949DA6',
              fontSize: '20px',
              '&:hover': { color: '#14171F' },
            }}
          />
        </Box>
        <Box className="eft-filter-modal-content">
          <Box className="eft-filter-grid">
            <Box className="eft-filter-item">
              <label className="eft-filter-label" htmlFor="eft-filter-start-date">
                Start Date
              </label>
              <input
                id="eft-filter-start-date"
                type="date"
                className="eft-filter-text-input"
                value={uiTransferFilters.startDate}
                max={uiTransferFilters.endDate || undefined}
                onChange={event => updateUiTransferFilter('startDate', event.target.value)}
              />
            </Box>

            <Box className="eft-filter-item">
              <label className="eft-filter-label" htmlFor="eft-filter-end-date">
                End Date
              </label>
              <input
                id="eft-filter-end-date"
                type="date"
                className="eft-filter-text-input"
                value={uiTransferFilters.endDate}
                min={uiTransferFilters.startDate || undefined}
                onChange={event => updateUiTransferFilter('endDate', event.target.value)}
              />
            </Box>

            <Box className="eft-filter-item eft-filter-item-full">
              <label className="eft-filter-label">Status</label>
              <FormControl sx={{ width: '100%' }}>
                <Select
                  value={uiTransferFilters.status}
                  onChange={event => updateUiTransferFilter('status', event.target.value)}
                  displayEmpty
                  className="eft-filter-select"
                  IconComponent={KeyboardArrowDownIcon}
                  sx={{ '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
                >
                  <MenuItem value="" className="eft-dropdown-option">
                    All
                  </MenuItem>
                  <MenuItem value="pending" className="eft-dropdown-option">
                    Pending
                  </MenuItem>
                  <MenuItem value="transferred" className="eft-dropdown-option">
                    Transferred
                  </MenuItem>
                  <MenuItem value="failed" className="eft-dropdown-option">
                    Failed
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box className="eft-filter-item">
              <label className="eft-filter-label" htmlFor="eft-filter-min-amount">
                Min Total Amount (QAR)
              </label>
              <input
                id="eft-filter-min-amount"
                type="number"
                min="0"
                placeholder="Min"
                className="eft-filter-text-input"
                value={uiTransferFilters.minAmount}
                onChange={event => updateUiTransferFilter('minAmount', event.target.value)}
              />
            </Box>

            <Box className="eft-filter-item">
              <label className="eft-filter-label" htmlFor="eft-filter-max-amount">
                Max Total Amount (QAR)
              </label>
              <input
                id="eft-filter-max-amount"
                type="number"
                min="0"
                placeholder="Max"
                className="eft-filter-text-input"
                value={uiTransferFilters.maxAmount}
                onChange={event => updateUiTransferFilter('maxAmount', event.target.value)}
              />
            </Box>
          </Box>
        </Box>
        <Box className="eft-filter-buttons-container">
          <Button
            variant="outlined"
            onClick={clearTransferFilters}
            className="eft-filter-clear-button"
          >
            Clear All
          </Button>
          <Button
            variant="contained"
            onClick={applyTransferFilters}
            className="eft-filter-apply-button"
          >
            Apply
          </Button>
        </Box>
      </Popover>

      <TransactionDetailsModal
        open={Boolean(editTransfer)}
        transfer={editTransfer}
        isSaving={isSaving}
        onClose={() => setEditTransfer(null)}
        onSubmit={handleSaveTransaction}
      />

      <Dialog
        open={Boolean(deleteTransfer)}
        onClose={() => setDeleteTransfer(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle className="eft-dialog-title">
          Delete {deleteTransfer?.referenceNo}?
        </DialogTitle>
        <DialogContent>
          <Typography className="eft-dialog-text">
            This removes the transfer and its {deleteTransfer?.itemCount} payments, which return to
            the Eligible tab.
          </Typography>
          <Typography className="eft-dialog-note">
            There is no undo. If the money was already sent, the bank reference and the record of
            what it covered are lost with it.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '12px 24px 20px' }}>
          <Button onClick={() => setDeleteTransfer(null)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            sx={{ textTransform: 'none' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle className="eft-dialog-title">Claim payments into an EFT?</DialogTitle>
        <DialogContent>
          <Typography className="eft-dialog-text">
            {claimsWholeFilter
              ? `All ${claimCount} payments matching the current filter will be grouped into one transfer, totalling QAR ${formatAmount(displayTotal)}.`
              : `${claimCount} selected payment${claimCount === 1 ? '' : 's'} will be grouped into one transfer, totalling QAR ${formatAmount(displayTotal)}.`}
          </Typography>
          <Typography className="eft-dialog-note">
            They leave the Eligible tab straight away. Deleting the transfer brings them back.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '12px 24px 20px' }}>
          <Button onClick={() => setConfirmOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmClaim}
            variant="contained"
            sx={{ textTransform: 'none', backgroundColor: '#0a5ef3' }}
          >
            Claim
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default EFT;
